import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  useStructureVersions, useCreateStructureVersion, useActivateStructureVersion,
  useSections, useCreateSection, useUpdateSection, useDeleteSection,
} from "../../../lib/presentationQueries";
import { useAutosaveEntity } from "../../../hooks/useAutosaveEntity";
import AutosaveField from "./AutosaveField";
import AutosaveStatus from "./AutosaveStatus";
import PromptDialog from "./PromptDialog";
import ConfirmDialog from "./ConfirmDialog";
import InlineFileAttachments from "../files/InlineFileAttachments";

function SectionRow({ workspaceId, presId, section, onDeleteRequested }) {
  const updateSection = useUpdateSection(workspaceId, presId);
  const auto = useAutosaveEntity({
    initialValues: { title: section.title || "" },
    revision: section.revision,
    onSave: (payload) => updateSection.mutateAsync({ sectionId: section.section_id, ...payload }),
  });
  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg px-3 py-2" data-testid={`structure-section-${section.section_id}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1"><AutosaveField value={auto.values.title} onChange={(v) => auto.setField("title", v)} testId={`structure-section-${section.section_id}-title`} /></div>
        <AutosaveStatus status={auto.status} onRetry={auto.retry} testId={`structure-section-${section.section_id}-autosave-status`} />
        <button onClick={() => onDeleteRequested(section.section_id)} className="text-ink-soft hover:text-terracotta shrink-0" aria-label="Delete section" data-testid={`structure-section-${section.section_id}-delete`}>
          <Trash2 size={15} />
        </button>
      </div>
      <div className="mt-1.5">
        <InlineFileAttachments
          workspaceId={workspaceId} targetType="section" targetId={`${presId}:${section.section_id}`}
          label={`the section "${section.title || "Untitled"}"`} compact
        />
      </div>
    </div>
  );
}

export default function StructureArea({ workspaceId, presId, activeStructureVersionId }) {
  const [selectedSvId, setSelectedSvId] = useState(null);
  const [svDialogOpen, setSvDialogOpen] = useState(false);
  const [pendingDeleteSectionId, setPendingDeleteSectionId] = useState(null);
  const { data: svData } = useStructureVersions(workspaceId, presId);
  const versions = svData?.items || [];
  const svId = selectedSvId || activeStructureVersionId || versions[0]?.sv_id;
  const { data: sectionsData } = useSections(workspaceId, presId, svId);
  const createStructureVersion = useCreateStructureVersion(workspaceId, presId);
  const activateStructureVersion = useActivateStructureVersion(workspaceId, presId);
  const createSection = useCreateSection(workspaceId, presId, svId);
  const deleteSection = useDeleteSection(workspaceId, presId, svId);
  const sections = sectionsData?.items || [];

  return (
    <div data-testid="presentation-structure-area">
      <h2 className="font-serif text-xl text-ink mb-4">Structure</h2>
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="structure-version-switcher">
        {versions.map((v) => (
          <button
            key={v.sv_id}
            onClick={() => { setSelectedSvId(v.sv_id); if (!v.is_active) activateStructureVersion.mutate(v.sv_id); }}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${v.is_active ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-ink/15 text-ink-soft hover:border-ink/30"}`}
            data-testid={`structure-version-${v.sv_id}`}
          >
            {v.label}{v.is_active && " · active"}
          </button>
        ))}
        <button
          onClick={() => setSvDialogOpen(true)}
          className="text-xs px-3 py-1.5 rounded-full border border-dashed border-ink/25 text-ink-soft hover:border-terracotta hover:text-terracotta"
          data-testid="structure-version-add"
        >
          <Plus size={12} className="inline mr-1" /> New structure
        </button>
      </div>

      <div className="space-y-2 mb-4" data-testid="structure-sections-list">
        {sections.map((s) => (
          <SectionRow key={s.section_id} workspaceId={workspaceId} presId={presId} section={s} onDeleteRequested={setPendingDeleteSectionId} />
        ))}
        {sections.length === 0 && <p className="text-ink-soft text-sm">No sections yet.</p>}
      </div>
      <button
        onClick={() => createSection.mutate("New section")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-terracotta hover:text-terracotta/80"
        data-testid="structure-add-section"
      >
        <Plus size={15} /> Add section
      </button>

      <PromptDialog
        open={svDialogOpen} onOpenChange={setSvDialogOpen} title="Name this structure"
        placeholder="e.g. Story first" confirmLabel="Create"
        onConfirm={(label) => createStructureVersion.mutate(label)} testId="structure-version-create-dialog"
      />
      <ConfirmDialog
        open={!!pendingDeleteSectionId}
        onOpenChange={(o) => !o && setPendingDeleteSectionId(null)}
        title="Delete this section?"
        description="InterAct saves an automatic checkpoint first, so this — and its beats — can always be restored from Version History."
        confirmLabel="Delete"
        onConfirm={() => { deleteSection.mutate(pendingDeleteSectionId); setPendingDeleteSectionId(null); }}
        testId="structure-section-delete-confirm"
      />
    </div>
  );
}
